import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const governanceSections = [
  {
    id: 'governance-summary',
    letter: 'A',
    title: 'Governance summary',
    intro: 'Internal-only platform-admin governance dossier for readiness preparation and lab-stage review. It summarizes current control coverage and the follow-up work still needed before any formal discussion.',
    items: [
      {
        area: 'Governance summary',
        component: 'Internal-only platform admin area',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the internal portal split visible and never merge it back into the customer workspace.',
      },
      {
        area: 'Governance summary',
        component: 'Purpose of governance pack',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Current page',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Use this dossier as internal evidence preparation, not as an official report.',
      },
      {
        area: 'Governance summary',
        component: 'Current maturity',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open demo runner',
        status: 'Internal lab',
        risk: 'Low',
        nextAction: 'Keep the maturity label as internal readiness / lab stage.',
      },
      {
        area: 'Governance summary',
        component: 'What is implemented',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Open architecture',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Continue linking evidence back to the live internal components.',
      },
      {
        area: 'Governance summary',
        component: 'What still needs validation or formal policy work',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Draft',
        risk: 'High',
        nextAction: 'Define the remaining policies, validation points, and evidence gaps.',
      },
    ],
  },
  {
    id: 'access-control-governance',
    letter: 'B',
    title: 'Access control governance',
    intro: 'Evidence that normal workspace access and internal platform-admin access remain separated, with strict internal route and API boundaries.',
    items: [
      {
        area: 'Access control governance',
        component: 'PlatformAdminOnly frontend boundary',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Current page',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the internal layout and page guards exclusive to platform admins.',
      },
      {
        area: 'Access control governance',
        component: 'requirePlatformAdmin backend boundary',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Architecture',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep backend admin APIs mounted only under strict internal prefixes.',
      },
      {
        area: 'Access control governance',
        component: 'Separation between normal workspace and internal admin area',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Preserve the normal dashboard and internal admin portal as distinct surfaces.',
      },
      {
        area: 'Access control governance',
        component: 'No internal navigation in normal DashboardLayout',
        linkTo: '/dashboard',
        linkLabel: 'Open workspace',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep internal links out of the customer workspace navigation.',
      },
      {
        area: 'Access control governance',
        component: 'Strict internal API prefixes',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Internal lab entry points',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Avoid broad /api mounting for internal routers.',
      },
      {
        area: 'Access control governance',
        component: 'Normal org admins blocked from internal routes',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Authenticator preview',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the platform-admin-only boundary strict and predictable.',
      },
      {
        area: 'Access control governance',
        component: 'No hardcoded user/email bypasses',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Digital Key Demo Runner',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Continue using role and route checks only.',
      },
    ],
  },
  {
    id: 'audit-event-governance',
    letter: 'C',
    title: 'Audit and event trail governance',
    intro: 'Internal event-trail coverage for identity, account, login-request, and admin activity, using sanitized metadata only.',
    items: [
      {
        area: 'Audit/event trail governance',
        component: 'Identity lifecycle events',
        linkTo: '/internal/identities',
        linkLabel: 'Identity operations',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep lifecycle events readable and safe without raw evidence.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Digital key account events',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Digital key accounts',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep account events internal and metadata-only.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Login request and authenticator events',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'OIDC lab',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep request, approval, and return events aligned through safe IDs.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Admin view events',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Digital Key Demo Runner',
        status: 'Partial',
        risk: 'Medium',
        nextAction: 'Document any admin-view or reviewer-visibility audit trail more explicitly.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Event timestamps',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Current page',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Continue showing created/updated timestamps in internal summaries.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Reason capture for lifecycle actions',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep reason capture mandatory where lifecycle actions require it.',
      },
      {
        area: 'Audit/event trail governance',
        component: 'Safe metadata only',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Safe lab output',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Do not surface raw evidence or sensitive tokens in the audit trail.',
      },
    ],
  },
  {
    id: 'identity-lifecycle-governance',
    letter: 'D',
    title: 'Identity lifecycle governance',
    intro: 'Identity lifecycle controls for internal operations and auditability, with the record remaining tied to safe internal metadata.',
    items: [
      {
        area: 'Identity lifecycle governance',
        component: 'Create identity',
        linkTo: '/internal/identities',
        linkLabel: 'Open identities',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep creation tied to approved verification-derived source records.',
      },
      {
        area: 'Identity lifecycle governance',
        component: 'Suspend identity',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Partial',
        risk: 'Medium',
        nextAction: 'Continue capturing a reason and preserve the review trail.',
      },
      {
        area: 'Identity lifecycle governance',
        component: 'Reactivate identity',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Draft',
        risk: 'High',
        nextAction: 'Define the approval path and authorization rules before formalizing reactivation.',
      },
      {
        area: 'Identity lifecycle governance',
        component: 'Revoke identity',
        linkTo: '/internal/identities',
        linkLabel: 'Identity timeline',
        status: 'Partial',
        risk: 'High',
        nextAction: 'Confirm terminal revocation behavior and downstream effects.',
      },
      {
        area: 'Identity lifecycle governance',
        component: 'Reason requirements',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Implemented',
        risk: 'Medium',
        nextAction: 'Continue requiring reason capture for impactful lifecycle actions.',
      },
      {
        area: 'Identity lifecycle governance',
        component: 'Event timeline',
        linkTo: '/internal/identities',
        linkLabel: 'Identity timeline',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the timeline safe and readable.',
      },
    ],
  },
  {
    id: 'data-minimization-governance',
    letter: 'E',
    title: 'Data minimization governance',
    intro: 'Controls to ensure the internal portal stays metadata-only and never exposes raw evidence or secrets in operational views.',
    items: [
      {
        area: 'Data minimization governance',
        component: 'No raw identity evidence in internal dashboards',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Digital key accounts',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep overview cards and tables limited to safe metadata.',
      },
      {
        area: 'Data minimization governance',
        component: 'No document / selfie images in evidence pages',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep all evidence packs narrative-only.',
      },
      {
        area: 'Data minimization governance',
        component: 'No RRN/BIS in current scope',
        linkTo: '/internal/regulated-identifiers',
        linkLabel: 'Regulated identifiers',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep regulated identifiers absent unless a separate legal path exists.',
      },
      {
        area: 'Data minimization governance',
        component: 'No OCR/MRZ raw data shown',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Continue minimizing extraction outputs and raw payloads.',
      },
      {
        area: 'Data minimization governance',
        component: 'No API keys, webhook secrets, tokens, or private keys shown',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'OIDC lab',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep lab outputs short-lived and hidden from overview pages.',
      },
      {
        area: 'Data minimization governance',
        component: 'Safe metadata only in internal overview',
        linkTo: '/internal',
        linkLabel: 'Internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Avoid expanding summary cards into sensitive record detail.',
      },
      {
        area: 'Data minimization governance',
        component: 'Image retention settings and DPIA placeholder',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Draft',
        risk: 'High',
        nextAction: 'Document retention, DPIA, and deletion expectations before external discussion.',
      },
    ],
  },
  {
    id: 'operational-governance',
    letter: 'F',
    title: 'Operational governance',
    intro: 'Operating model evidence for the internal portal, including monitoring, support, and internal demo operations.',
    items: [
      {
        area: 'Operational governance',
        component: 'Internal operations dashboard',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the dashboard focused on internal operations only.',
      },
      {
        area: 'Operational governance',
        component: 'Onboarding monitoring',
        linkTo: '/internal',
        linkLabel: 'Internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Preserve safe summary metrics and onboarding status only.',
      },
      {
        area: 'Operational governance',
        component: 'Verification review workflow',
        linkTo: '/internal/identities',
        linkLabel: 'Identity operations',
        status: 'Implemented',
        risk: 'Medium',
        nextAction: 'Continue reviewing verification-derived identities through internal staff only.',
      },
      {
        area: 'Operational governance',
        component: 'Digital key demo runner',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Digital Key Demo Runner',
        status: 'Internal lab',
        risk: 'Low',
        nextAction: 'Use the demo runner as the internal runbook for guided lab demos.',
      },
      {
        area: 'Operational governance',
        component: 'Support / recovery placeholder',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Partial',
        risk: 'Medium',
        nextAction: 'Document support and recovery ownership before any external pilot.',
      },
      {
        area: 'Operational governance',
        component: 'Incident response placeholder',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Draft',
        risk: 'High',
        nextAction: 'Write an incident response outline and escalation path.',
      },
      {
        area: 'Operational governance',
        component: 'Availability / monitoring placeholder',
        linkTo: '/internal/partner-technical-readiness',
        linkLabel: 'Partner readiness',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Document uptime, alerting, and service-health expectations.',
      },
    ],
  },
  {
    id: 'change-release-governance',
    letter: 'G',
    title: 'Change and release governance',
    intro: 'The current change and release process is intentionally lightweight. This section captures what is already in use and what remains informal.',
    items: [
      {
        area: 'Change and release governance',
        component: 'Code changes through git commits',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Governance pack',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep commits small and reviewable.',
      },
      {
        area: 'Change and release governance',
        component: 'Frontend/backend validation checks',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Digital Key Demo Runner',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Continue validating the affected surfaces after each change.',
      },
      {
        area: 'Change and release governance',
        component: 'Build checks',
        linkTo: '/internal',
        linkLabel: 'Internal dashboard',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep the frontend build green before sharing internal evidence.',
      },
      {
        area: 'Change and release governance',
        component: 'Backend tests where relevant',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Partial',
        risk: 'Medium',
        nextAction: 'Run backend checks when backend route or auth surfaces change.',
      },
      {
        area: 'Change and release governance',
        component: 'Deployment notes',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Document release notes and approval handoff more formally.',
      },
      {
        area: 'Change and release governance',
        component: 'Manual test checklists',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Demo runner',
        status: 'Implemented',
        risk: 'Low',
        nextAction: 'Keep manual checks tied to the current internal flow.',
      },
      {
        area: 'Change and release governance',
        component: 'Known limitation: not yet a formal change-control process',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Readiness matrix',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Define a formal change-control approach before treating this as an external governance artefact.',
      },
    ],
  },
  {
    id: 'open-gaps',
    letter: 'H',
    title: 'Open governance gaps',
    intro: 'A short list of the governance topics that still need explicit policy work or validation.',
    items: [
      {
        area: 'Open governance gaps',
        component: 'Formal incident response policy',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        risk: 'High',
        nextAction: 'Write the incident response policy and escalation mapping.',
      },
      {
        area: 'Open governance gaps',
        component: 'DPIA / GDPR documentation',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        risk: 'High',
        nextAction: 'Prepare a DPIA outline and privacy review notes.',
      },
      {
        area: 'Open governance gaps',
        component: 'Security audit / penetration test',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        risk: 'High',
        nextAction: 'Schedule security review and capture results.',
      },
      {
        area: 'Open governance gaps',
        component: 'Formal access review procedure',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Define reviewer ownership and access-review cadence.',
      },
      {
        area: 'Open governance gaps',
        component: 'Retention policy finalization',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        risk: 'High',
        nextAction: 'Finalize retention windows and deletion handling.',
      },
      {
        area: 'Open governance gaps',
        component: 'BOSA/FAS technical and security evidence requirements',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Needs validation',
        risk: 'High',
        nextAction: 'Confirm the evidence shape and technical/security expectations with BOSA/FAS.',
      },
      {
        area: 'Open governance gaps',
        component: 'Support and recovery process',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Document support ownership, recovery, and escalation paths.',
      },
      {
        area: 'Open governance gaps',
        component: 'Formal monitoring / alerting',
        linkTo: '/internal/partner-technical-readiness',
        linkLabel: 'Partner readiness',
        status: 'Draft',
        risk: 'Medium',
        nextAction: 'Write an operations baseline for monitoring and alerting.',
      },
    ],
  },
];

const statusStyles = {
  Implemented: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  'Internal lab': 'border-sky-300 bg-sky-50 text-sky-800',
  Partial: 'border-amber-300 bg-amber-50 text-amber-800',
  Draft: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  'Needs validation': 'border-rose-300 bg-rose-50 text-rose-700',
  'Not started': 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const riskStyles = {
  Low: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Medium: 'border-amber-200 bg-amber-50 text-amber-800',
  High: 'border-rose-200 bg-rose-50 text-rose-700',
};

function Badge({ value, styles, fallback = 'border-zinc-200 bg-zinc-50 text-zinc-600' }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles[value] || fallback}`}>{value}</span>;
}

function SummaryCard({ label, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function GovernanceSection({ section }) {
  return (
    <section className="border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{section.letter}</p>
          <h2 className="mt-1 font-display text-lg font-semibold">{section.title}</h2>
          <p className="mt-2 max-w-4xl text-sm text-zinc-600">{section.intro}</p>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[72rem] text-sm">
          <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="py-2 pr-4 font-medium">Governance area</th>
              <th className="py-2 pr-4 font-medium">Current control</th>
              <th className="py-2 pr-4 font-medium">Internal evidence link</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Risk</th>
              <th className="py-2 font-medium">Next action</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item) => (
              <tr key={`${section.id}-${item.component}`} className="border-b border-zinc-100 align-top last:border-b-0">
                <td className="py-3 pr-4 font-medium text-zinc-800">{item.area}</td>
                <td className="py-3 pr-4 text-zinc-600">{item.component}</td>
                <td className="py-3 pr-4">
                  <Link to={item.linkTo} className="text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
                    {item.linkLabel}
                  </Link>
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.status} styles={statusStyles} />
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.risk} styles={riskStyles} />
                </td>
                <td className="py-3 text-zinc-600">{item.nextAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AuditGovernancePack() {
  const summary = useMemo(() => {
    const items = governanceSections.flatMap((section) => section.items);
    return {
      accessControls: items.filter((item) => item.area === 'Access control governance' && item.status === 'Implemented').length,
      auditCoverage: items.filter((item) => item.area === 'Audit/event trail governance' && ['Implemented', 'Partial'].includes(item.status)).length,
      dataMinimization: items.filter((item) => item.area === 'Data minimization governance' && item.status === 'Implemented').length,
      highPriorityGaps: items.filter((item) => ['Draft', 'Not started', 'Needs validation'].includes(item.status) || item.risk === 'High').length,
    };
  }, []);

  return (
    <div data-testid="audit-governance-pack-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Audit & Governance Pack" showDashboard={false}/>

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Audit &amp; Governance Pack v0.2</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Internal readiness dossier for governance, audit, and operational controls. It is for internal preparation only and does not claim any official certification, approval, or compliance status.
        </p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-medium">Internal governance dossier only</p>
        <p className="mt-1">
          This page is not a public audit report, DPIA, legal opinion, security certification, FAS/CSAM integration statement, or official LoA claim.
        </p>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Access controls documented" value={summary.accessControls} detail="Internal access-control boundaries and platform-admin separation." />
        <SummaryCard label="Audit / lifecycle coverage" value={summary.auditCoverage} detail="Identity, account, login-request, and approval event coverage." />
        <SummaryCard label="Data minimization controls" value={summary.dataMinimization} detail="Safe metadata-only views and raw-data suppression." />
        <SummaryCard label="High-priority governance gaps" value={summary.highPriorityGaps} detail="Draft, not-started, and validation-dependent controls." />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">How to read this pack</h2>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Each section links to the internal pages that already demonstrate the control in practice. The controls remain internal, lab-stage, and safe metadata-only.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="/internal/digital-key-demo" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Demo</Link>
          <Link to="/internal/fas-readiness" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">FAS Readiness</Link>
          <Link to="/internal/bosa-evidence-pack" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">BOSA Evidence Pack</Link>
          <Link to="/internal/digital-key-architecture" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Architecture</Link>
          <Link to="/internal/identities" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Identities</Link>
          <Link to="/internal/digital-key-accounts" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Accounts</Link>
          <Link to="/internal/oidc-lab" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">OIDC Lab</Link>
          <Link to="/internal/authenticator-preview" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Authenticator Preview</Link>
          <Link to="/internal/partner-technical-readiness" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Partner Technical Readiness</Link>
          <Link to="/internal/relying-party-profiles" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Relying Party Profiles</Link>
          <Link to="/internal/regulated-identifiers" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Regulated Identifiers</Link>
        </div>
      </section>

      {governanceSections.map((section) => (
        <div key={section.id} className="mt-6">
          <GovernanceSection section={section} />
        </div>
      ))}

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Open governance gaps</h2>
          <ul className="mt-4 space-y-2 text-sm text-zinc-600">
            {governanceSections.find((section) => section.id === 'open-gaps').items.map((item) => (
              <li key={item.component} className="border-b border-zinc-100 pb-2 last:border-b-0">{item.component}</li>
            ))}
          </ul>
        </section>
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Current limitations</h2>
          <ul className="mt-4 space-y-2 text-sm text-zinc-600">
            <li>Formal incident response policy still needs to be written.</li>
            <li>DPIA / GDPR documentation is still a placeholder.</li>
            <li>Security audit and access review procedures still need formalization.</li>
            <li>Retention policy finalization remains outstanding.</li>
            <li>BOSA/FAS evidence requirements still need validation.</li>
          </ul>
        </section>
      </div>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Manual validation checklist</h2>
        <ol className="mt-4 space-y-2 text-sm text-zinc-600">
          <li>1. Confirm `/internal` remains platform-admin-only.</li>
          <li>2. Confirm the normal workspace navigation does not include internal links.</li>
          <li>3. Confirm internal pages show only safe metadata and no raw evidence.</li>
          <li>4. Confirm audit/lifecycle actions still require internal admin access.</li>
          <li>5. Confirm build and diff checks are clean before sharing evidence internally.</li>
        </ol>
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Related internal references</h2>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">Identity Operations</Link>
            <Link to="/internal/digital-key-accounts" className="border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Accounts</Link>
            <Link to="/internal/oidc-lab" className="border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">OIDC Lab</Link>
            <Link to="/internal/authenticator-preview" className="border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">Authenticator Preview</Link>
            <Link to="/internal/digital-key-demo" className="border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Demo</Link>
          </div>
        </section>
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">What this pack does not claim</h2>
          <ul className="mt-4 space-y-2 text-sm text-zinc-600">
            <li>No official audit report, certification, or compliance statement.</li>
            <li>No official FAS/CSAM integration, approval, or LoA claim.</li>
            <li>No public login product or production OIDC/OAuth surface.</li>
            <li>No raw evidence, images, selfies, OCR/MRZ raw data, tokens, or secrets are shown.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
